import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion } from 'framer-motion';
import { AlertTriangle, TrendingDown, TrendingUp } from 'lucide-react';

const AnomalyPredictor = ({ filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [anomalyData, setAnomalyData] = useState({ detected_anomalies: [], predicted_anomalies: [], baseline: {} });
  const [loading, setLoading] = useState(true);
  const animationDuration = reducedMotion ? 0 : 0.5;

  useEffect(() => {
    fetchAnomalyData();
    const interval = setInterval(fetchAnomalyData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [filters]);

  const fetchAnomalyData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/insights/v2/anomalies?lookback_hours=168`);
      const result = await response.json();
      setAnomalyData(result);
    } catch (error) {
      console.error('Error fetching anomalies:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return { bg: '#7f1d1d', border: '#dc2626', text: '#ef4444' };
      case 'medium': return { bg: '#78350f', border: '#f59e0b', text: '#fbbf24' };
      default: return { bg: '#164e63', border: '#C9A24A', text: '#22d3ee' };
    }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <AlertTriangle className="w-5 h-5 text-orange-400" />
          Anomaly Detector
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Early warning system • ML-based anomaly detection
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-slate-400">Analyzing patterns...</div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Detected Anomalies */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                Detected Anomalies ({anomalyData.detected_anomalies.length})
              </h4>
              {anomalyData.detected_anomalies.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-4">No anomalies detected in recent data</p>
              ) : (
                <div className="space-y-2">
                  {anomalyData.detected_anomalies.map((anomaly, index) => {
                    const colors = getSeverityColor(anomaly.severity);
                    return (
                      <motion.div
                        key={index}
                        className="rounded-lg p-4 border"
                        style={{ backgroundColor: colors.bg, borderColor: colors.border }}
                        initial={reducedMotion ? {} : { opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {anomaly.type === 'drop' ? (
                                <TrendingDown className="w-4 h-4" style={{ color: colors.text }} />
                              ) : (
                                <TrendingUp className="w-4 h-4" style={{ color: colors.text }} />
                              )}
                              <span className="text-sm font-semibold text-white">
                                {anomaly.type === 'drop' ? 'Performance Drop' : 'Spike'}
                              </span>
                            </div>
                            <p className="text-xs text-slate-300">
                              Value: {(anomaly.value * 100).toFixed(2)}% at {new Date(anomaly.timestamp).toLocaleString()}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              Z-Score: {anomaly.z_score} ({anomaly.severity} severity)
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Predicted Anomalies */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                Predicted Anomalies ({anomalyData.predicted_anomalies.length})
              </h4>
              {anomalyData.predicted_anomalies.length === 0 ? (
                <p className="text-xs text-green-400 text-center py-4 bg-green-500/10 rounded-lg border border-green-500/20">
                  ✓ No anomalies predicted in next 24 hours
                </p>
              ) : (
                <div className="space-y-2">
                  {anomalyData.predicted_anomalies.map((anomaly, index) => {
                    const colors = getSeverityColor(anomaly.predicted_severity);
                    return (
                      <motion.div
                        key={index}
                        className="rounded-lg p-4 border"
                        style={{ backgroundColor: colors.bg, borderColor: colors.border }}
                        initial={reducedMotion ? {} : { opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold text-white">Predicted Issue</span>
                              <span className="text-xs px-2 py-0.5 rounded bg-white/20" style={{ color: colors.text }}>
                                {(anomaly.probability * 100).toFixed(0)}% probability
                              </span>
                            </div>
                            <p className="text-xs text-slate-300">
                              Expected: {new Date(anomaly.timestamp).toLocaleString()}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              {anomaly.reason}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Baseline Stats */}
            {anomalyData.baseline && (
              <div className="mt-6 grid grid-cols-3 gap-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="text-center">
                  <p className="text-xl font-bold text-gold">
                    {(anomalyData.baseline.mean * 100).toFixed(2)}%
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Baseline Mean</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-ink-500">
                    ±{(anomalyData.baseline.std_dev * 100).toFixed(2)}%
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Std Deviation</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-emerald-400">{anomalyData.baseline.sample_hours}</p>
                  <p className="text-xs text-slate-400 mt-1">Sample Hours</p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AnomalyPredictor;
